import React, { useState, useEffect } from 'react';
import {
  Box, Card, Grid, Typography, Button, Chip, TextField, MenuItem, IconButton, Tooltip
} from '@mui/material';
import {
  Email, Phone, AssignmentInd, Work, Business, LocationOn, HomeWork, Apartment
} from '@mui/icons-material';
import { entrepriseCandidatureStatus, recruteurCandidatureStatus } from '../commun/constants/options';
import LoadingPopup from "../commun/LoadingPopup";
import SummaryIABox from "../commun/SummaryIABox";
import './CandidatureDetailsCommon.css';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';

const CandidatureDetailsCommon = ({
  HeaderComponent,
  headerProps,
  candidature,
  editedStatut,
  setEditedStatut,
  messageEntreprise,
  setMessageEntreprise,
  dateEntretien,
  setDateEntretien,
  onSave,
  onCancel,
  editable = false,
  API_BASE,
  iaSummary,
}) => {
  const [errorRaisonRefus, setErrorRaisonRefus] = useState('');
  const [errorDateEntretien, setErrorDateEntretien] = useState('');

  const [similarityScore, setSimilarityScore] = useState(null);
  const [loadingSimilarity, setLoadingSimilarity] = useState(false);

  useEffect(() => {
    const fetchSimilarity = async () => {
      if (
        !candidature?.missionId ||
        !candidature?.candidatId?._id ||
        similarityScore !== null
      ) return;
  
      try {
        setLoadingSimilarity(true);
        const response = await fetch(
          `${API_BASE}/api/matching/similarity/${candidature.missionId}/${candidature.candidatId._id}`
        );
  
        const data = await response.json();
  
        if (response.ok && typeof data.similarity === 'number') {
          setSimilarityScore(data.similarity);
        } else {
          console.warn("⚠️ Aucune similarité trouvée :", data.message || data);
        }
      } catch (error) {
        console.error("💥 Erreur lors du calcul de similarité :", error);
      } finally {
        setLoadingSimilarity(false);
      }
    };
  
    fetchSimilarity();
  }, [candidature?.missionId, candidature?.candidatId?._id, API_BASE]);
  
  if (!candidature)
    return <LoadingPopup open={!candidature} message="Chargement..." />;

  const devise = candidature?.devise || '€';
  const { candidatId, recruteurId, recruteur, mission, entreprise } = candidature;
  const reference = candidatId?.reference || 'N/A';
  const tarif = candidature?.tarif || '';
  const cvFileUrl = candidature?.cvId?.fileUrl || null;
  const recruiter = recruteur || recruteurId || {};
  const recruiterName = recruiter.nomComplet || `${recruiter.firstName || ''} ${recruiter.lastName || ''}`.trim();

  // Rôle
  const userRole = headerProps?.userRole;
  const isRecruteur = userRole === 'recruteur';
  const isEntreprise = userRole === 'entreprise';

  // Mapping dynamique selon le rôle
  const statusOptions = isEntreprise ? entrepriseCandidatureStatus : recruteurCandidatureStatus;
  const matchedStatus = statusOptions.find((s) => s.value === editedStatut);

  // Label dynamique pour le chip
  let displayStatutLabel = matchedStatus ? matchedStatus.label : editedStatut;

  // Correction du label pour cohérence contextuelle
  if (isEntreprise && (editedStatut === "A traiter" || editedStatut === "Candidature envoyée"))
    displayStatutLabel = "Candidature à traiter";
  if (isRecruteur && (editedStatut === "A traiter" || editedStatut === "Candidature à traiter"))
    displayStatutLabel = "Candidature envoyée";

  // Gère la validation du formulaire
  const handleSave = () => {
    let valid = true;
    setErrorRaisonRefus('');
    setErrorDateEntretien('');

    if (
      (editedStatut === 'Refusé avant entretien' || editedStatut === 'Non retenu après entretien') &&
      !messageEntreprise.trim()
    ) {
      setErrorRaisonRefus('La raison du refus est obligatoire.');
      valid = false;
    }
    if (editedStatut === 'Accepté pour entretien' && !dateEntretien) {
      setErrorDateEntretien("La date d'entretien est obligatoire.");
      valid = false;
    }
    if (!valid) return;
    onSave();
  };

  // Option "brouillon" masquée côté entreprise
  const filteredStatusOptions = statusOptions.filter(
    step =>
      step.value?.toLowerCase() !== 'brouillon' &&
      !(isEntreprise && step.value === 'A traiter')
  );

  return (
    <Box className="cdc2-root">
      {HeaderComponent && <HeaderComponent {...headerProps} />}
      <Grid container className="cdc2-main-grid">
        {/* Partie gauche : fiche candidat */}
        <Grid item xs={12} md={6} className="cdc2-grid-half">
          <Card className="cdc2-card">
            <Box className="cdc2-header-row">
              <Typography variant="h5" className="cdc2-title">
                Fiche candidat
              </Typography>
              <Chip
                label={displayStatutLabel}
                className={`cdc2-chip cdc2-chip-${matchedStatus?.value || 'default'}`}
              />
            </Box>
            {/* Référence + tarif + téléchargement */}
            <Box className="cdc2-info-row cdc2-ref-tarif-row" sx={{ alignItems: 'center' }}>
              <span>
                <span className="cdc2-label">Référence : </span>
                <span className="cdc2-ref-value">{reference}</span>
              </span>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={`Tarif : ${tarif ? tarif : 'N/A'} ${devise || ''}`}
                  className="cdc2-chip cdc2-chip-tarif"
                />
              
              </Box>
            </Box>

            <Box className="cdc2-info-row">
              <span className="cdc2-label">Date de candidature&nbsp;: </span>
              <span><strong>
                {(candidature.dateEnvoi) && !isNaN(new Date(candidature.dateEnvoi))
                  ? new Date(candidature.dateEnvoi).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : '—'}
              </strong></span>
            </Box>

            {/* Simple rappel du dossier de compétences */}
                <Box className="cdc2-info-row">
          
                  <span className="cdc2-label">Dossier de compétences&nbsp;: </span>

                  {cvFileUrl && (
                  <Tooltip title="Télécharger le dossier de compétences">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const win = window.open(`${API_BASE}${cvFileUrl}`, '_blank');
                        if (win) win.focus();
                      }}
                    >
                      <DownloadForOfflineIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                )}
                </Box>

                <Box className="cdc2-info-row">
                  <span className="cdc2-label">Score de matching&nbsp;:</span>
                  <span><strong>
                    {loadingSimilarity
                      ? 'Chargement...'
                      : similarityScore !== null
                        ? `${(similarityScore * 100).toFixed(1)}%`
                        : '—'}
                  </strong></span>
                </Box>
            {!isRecruteur && (
              <Box className="cdc2-form-row">
                <TextField
                  select
                  label="Modifier le statut"
                  value={editedStatut}
                  onChange={e => {
                    setEditedStatut(e.target.value);
                    setErrorRaisonRefus('');
                    setErrorDateEntretien('');
                  }}
                  fullWidth
                  margin="normal"
                  className="cdc2-status-select"
                >
                  {filteredStatusOptions.map((step) => (
                    <MenuItem key={step.value} value={step.value}>
                      {step.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {(editedStatut === 'Refusé avant entretien' || editedStatut === 'Non retenu après entretien') && (
              <TextField
                label="Raison du refus"
                value={messageEntreprise}
                onChange={e => {
                  setMessageEntreprise(e.target.value);
                  setErrorRaisonRefus('');
                }}
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                minRows={1}
                disabled={isRecruteur}
                error={!!errorRaisonRefus}
                helperText={errorRaisonRefus}
              />
            )}
            {editedStatut === 'Accepté pour entretien' && (
              <>
                <TextField
                  label="Message de l'entreprise"
                  value={messageEntreprise}
                  onChange={e => setMessageEntreprise(e.target.value)}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  multiline
                  minRows={1}
                  disabled={isRecruteur}
                />
                <TextField
                  label="Date d'entretien"
                  type="datetime-local"
                  value={dateEntretien ? new Date(dateEntretien).toISOString().slice(0, 16) : ''}
                  onChange={e => {
                    setDateEntretien(e.target.value);
                    setErrorDateEntretien('');
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  disabled={isRecruteur}
                  InputLabelProps={{ shrink: true }}
                  error={!!errorDateEntretien}
                  helperText={errorDateEntretien}
                />
              </>
            )}
            <Box className="cdc2-btn-row">
              <Button variant="outlined" color="primary" onClick={onCancel} className="cdc2-btn">Retour</Button>
              {editable && (
                <Button variant="contained" color="primary" onClick={handleSave} className="cdc2-btn">
                  Sauvegarder
                </Button>
              )}
            </Box>
          </Card>
        </Grid>
        {/* Partie droite : 2 cards alignées */}
        <Grid item xs={12} md={6} className="cdc2-grid-half">
          {/* Carte Infos Recruteur */}
          <Card className="recruiter-card">
            <Typography className="recruiter-title">Informations recruteur</Typography>
            <Box className="recruiter-info-row">
              <AssignmentInd className="recruiter-icon" />
              <span className="recruiter-label">Nom et prénom :</span>
              <span>{recruiterName || 'N/A'}</span>
            </Box>
            <Box className="recruiter-info-row">
              <Email className="recruiter-icon" />
              <span className="recruiter-label">Email :</span>
              <span>{recruiter.email || 'N/A'}</span>
            </Box>
            <Box className="recruiter-info-row">
              <Phone className="recruiter-icon" />
              <span className="recruiter-label">Téléphone :</span>
              <span>{recruiter.phone || 'N/A'}</span>
            </Box>
          </Card>
          <Card className="recruiter-card">
            <Typography className="recruiter-title">Informations mission</Typography>

            <Box className="recruiter-info-row">
              <Work className="recruiter-icon" />
              <span className="recruiter-label">Nom de la mission :</span>
              <span>{mission?.nomMission || 'N/A'}</span>
            </Box>

            {/* <Box className="recruiter-info-row">
              <Business className="recruiter-icon" />
              <span className="recruiter-label">Entreprise :</span>
              <span>{entreprise?.NomEntreprise || 'N/A'}</span>
            </Box> */}

            <Box className="recruiter-info-row">
              <LocationOn className="recruiter-icon" />
              <span className="recruiter-label">Lieu :</span>
              <span>{mission?.lieuxTravail || 'N/A'}</span>
            </Box>

            <Box className="recruiter-info-row">
              <HomeWork className="recruiter-icon" />
              <span className="recruiter-label">Mode de travail :</span>
              <span>{mission?.modeTravail || 'N/A'}</span>
            </Box>
{/* 
            <Box className="recruiter-info-row">
              <Apartment className="recruiter-icon" />
              <span className="recruiter-label">Secteur :</span>
              <span>{mission?.secteur || 'N/A'}</span>
            </Box> */}
          </Card>

          {/* Synthèse IA */}
          {/* {!isRecruteur && (
          <div className="summaryia-card">
            <SummaryIABox summary={iaSummary || "Aucune synthèse disponible."} />
          </div>
          )
        }  */}
          

        </Grid>
      </Grid>
    </Box>
  );
};

export default CandidatureDetailsCommon;
